// src/features/roles/components/RolePermissionsManager.jsx - Translated version
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Collapse,
  Button,
  Stack,
  useTheme,
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  Extension as ExtensionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { useRoles } from '../hooks/useRoles';
import { useRolePermissions } from '../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { departmentsAPI } from '../../departments/services/departments';

export default function RolePermissionsManager({ role, onUpdate }) {
  const theme = useTheme();
  const { t } = useTranslation(['roles', 'common']);

  const { showSuccess, showError } = useSnackbar();
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [rolePermissions, setRolePermissions] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [myDepartmentModules, setMyDepartmentModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({
    toAdd: [],
    toRemove: []
  });

  const {
    getRolePermissions,
    bulkAddPermissionsToRole,
    bulkRemovePermissionsFromRole
  } = useRoles();
  // Without role:update the permissions are only shown
  const { canUpdate } = useRolePermissions();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [modulesRes, rolePermsResult] = await Promise.all([
          departmentsAPI.getMyDepartmentModulesWithPermissions(),
          role ? getRolePermissions(role.role_id) : Promise.resolve(null),
        ]);

        const modules = modulesRes.data?.modules || [];
        setMyDepartmentModules(modules);

        const permissionsMap = {};
        modules.forEach(m => { permissionsMap[m.module_id] = m.permissions || []; });
        setAllPermissions(permissionsMap);

        if (rolePermsResult?.success) {
          setRolePermissions(rolePermsResult.data.permissions || []);
        }
      } catch {
        showError(t('roles:errors.permissions_load_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [role, getRolePermissions, showError, t]);

  const toggleModuleExpansion = useCallback((moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  }, []);

  // Apply all pending changes
  const applyPendingChanges = useCallback(async () => {
    if (!role || (pendingChanges.toAdd.length === 0 && pendingChanges.toRemove.length === 0)) {
      return;
    }

    setSaving(true);
    try {
      const promises = [];

      // Bulk add permissions
      if (pendingChanges.toAdd.length > 0) {
        promises.push(bulkAddPermissionsToRole(role.role_id, pendingChanges.toAdd));
      }

      // Bulk remove permissions
      if (pendingChanges.toRemove.length > 0) {
        promises.push(bulkRemovePermissionsFromRole(role.role_id, pendingChanges.toRemove));
      }

      const results = await Promise.all(promises);
      const allSuccessful = results.every(result => result.success);

      if (allSuccessful) {
        // Update local state
        setRolePermissions(prev => {
          let updated = [...prev];

          // Remove permissions that were removed
          if (pendingChanges.toRemove.length > 0) {
            updated = updated.filter(p => !pendingChanges.toRemove.includes(p.permission_id));
          }

          // Add new permissions
          if (pendingChanges.toAdd.length > 0) {
            const newPermissions = Object.values(allPermissions).flat()
              .filter(p => pendingChanges.toAdd.includes(p.permission_id));
            updated = [...updated, ...newPermissions];
          }

          return updated;
        });

        // Clear pending changes
        setPendingChanges({ toAdd: [], toRemove: [] });

        showSuccess(t('roles:success.permissions_applied', { 
          count: pendingChanges.toAdd.length + pendingChanges.toRemove.length 
        }));
        onUpdate?.();
      } else {
        showError(t('roles:errors.permissions_apply_failed'));
      }
    } catch (error) {
      showError(t('roles:errors.permissions_apply_failed'));
    } finally {
      setSaving(false);
    }
  }, [role, pendingChanges, bulkAddPermissionsToRole, bulkRemovePermissionsFromRole, allPermissions, showSuccess, showError, onUpdate, t]);

  // Discard pending changes
  const discardPendingChanges = useCallback(() => {
    setPendingChanges({ toAdd: [], toRemove: [] });
  }, []);

  const handleModuleToggle = useCallback(async (moduleId, isEnabled) => {
    if (!role) return;

    const modulePerms = allPermissions[moduleId] || [];
    const modulePermissionIds = modulePerms.map(p => p.permission_id);

    if (isEnabled) {
      // Module is currently enabled, disable it (remove all permissions)
      setPendingChanges(prev => ({
        toAdd: prev.toAdd.filter(id => !modulePermissionIds.includes(id)),
        toRemove: [...new Set([...prev.toRemove, ...modulePermissionIds])]
      }));
    } else {
      // Module is currently disabled, enable it (add all permissions)
      setPendingChanges(prev => ({
        toAdd: [...new Set([...prev.toAdd, ...modulePermissionIds])],
        toRemove: prev.toRemove.filter(id => !modulePermissionIds.includes(id))
      }));
    }
  }, [role, allPermissions]);

  const handlePermissionToggle = useCallback(async (permission, isEnabled) => {
    if (!role) return;

    if (isEnabled) {
      // Permission is currently enabled, add to remove list
      setPendingChanges(prev => ({
        toAdd: prev.toAdd.filter(id => id !== permission.permission_id),
        toRemove: [...new Set([...prev.toRemove, permission.permission_id])]
      }));
    } else {
      // Permission is currently disabled, add to add list
      setPendingChanges(prev => ({
        toAdd: [...new Set([...prev.toAdd, permission.permission_id])],
        toRemove: prev.toRemove.filter(id => id !== permission.permission_id)
      }));
    }
  }, [role]);

  const getEffectivePermissionState = useCallback((permissionId) => {
    const isCurrentlyAssigned = rolePermissions.some(p => p.permission_id === permissionId);
    const isPendingAdd = pendingChanges.toAdd.includes(permissionId);
    const isPendingRemove = pendingChanges.toRemove.includes(permissionId);

    if (isPendingAdd) return true;
    if (isPendingRemove) return false;
    return isCurrentlyAssigned;
  }, [rolePermissions, pendingChanges]);

  const getModulePermissionState = useCallback((moduleId) => {
    const modulePerms = allPermissions[moduleId] || [];

    if (modulePerms.length === 0) return { checked: false, indeterminate: false };

    const enabledCount = modulePerms.filter(p => getEffectivePermissionState(p.permission_id)).length;

    if (enabledCount === modulePerms.length) return { checked: true, indeterminate: false };
    if (enabledCount > 0) return { checked: false, indeterminate: true };
    return { checked: false, indeterminate: false };
  }, [allPermissions, getEffectivePermissionState]);

  const groupPermissionsByResource = useCallback((permissions) => {
    const grouped = {};
    permissions.forEach(permission => {
      if (!grouped[permission.resource]) {
        grouped[permission.resource] = [];
      }
      grouped[permission.resource].push(permission);
    });
    return grouped;
  }, []);

  const hasPendingChanges = pendingChanges.toAdd.length > 0 || pendingChanges.toRemove.length > 0;

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!role) {
    return (
      <Alert severity="info">
        {t('roles:messages.select_role_for_permissions')}
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <VpnKeyIcon color="primary" />
          <Box flex={1}>
            <Typography variant="h6">
              {t('roles:permissions_manager.title', { roleName: role.name })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('roles:permissions_manager.assigned_count', { 
                count: rolePermissions.length,
                defaultValue_plural: t('roles:permissions_manager.assigned_count_plural', { count: rolePermissions.length })
              })}
            </Typography>
          </Box>
        </Box>

        {/* Pending Changes Actions */}
        {hasPendingChanges && (
          <Box mb={3}>
            <Box 
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(239, 78, 78, 0.1) 0%, rgba(239, 78, 78, 0.05) 100%)',
                border: '1px solid',
                borderColor: 'warning.main',
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="start"
                gap={2}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                  >
                    {t('roles:permissions_manager.pending_changes')}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    {pendingChanges.toAdd.length > 0 && (
                      <Chip
                        icon={<AddIcon />}
                        label={t('roles:permissions_manager.to_add', { count: pendingChanges.toAdd.length })}
                        color="success"
                        variant="outlined"
                      />
                    )}
                    {pendingChanges.toRemove.length > 0 && (
                      <Chip
                        icon={<RemoveIcon />}
                        label={t('roles:permissions_manager.to_remove', { count: pendingChanges.toRemove.length })}
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={applyPendingChanges}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}
                    sx={{
                      bgcolor: theme.palette.warning.contrastText,
                      color: theme.palette.warning.main,
                      '&:hover': {
                        bgcolor: theme.palette.warning.contrastText,
                        opacity: 0.8
                      }
                    }}
                  >
                    {saving ? t('roles:actions.applying') : t('roles:actions.apply_changes')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={discardPendingChanges}
                    disabled={saving}
                  >
                    {t('roles:actions.discard')}
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Box>
        )}

        {myDepartmentModules.length === 0 ? (
          <Alert severity="info">
            {t('roles:messages.no_modules_enabled')}
          </Alert>
        ) : (
          <FormGroup>
            {myDepartmentModules.map((module, index) => {
              const modulePerms = allPermissions[module.module_id] || [];
              const isExpanded = expandedModules.has(module.module_id);
              const moduleState = getModulePermissionState(module.module_id);
              const groupedPermissions = groupPermissionsByResource(modulePerms);

              // Count current and effective permissions
              const currentCount = modulePerms.filter(p =>
                rolePermissions.some(rp => rp.permission_id === p.permission_id)
              ).length;
              const effectiveCount = modulePerms.filter(p =>
                getEffectivePermissionState(p.permission_id)
              ).length;

              return (
                <Box key={module.module_id}>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      py: 1,
                      px: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={moduleState.checked}
                          indeterminate={moduleState.indeterminate}
                          onChange={() => handleModuleToggle(module.module_id, moduleState.checked || moduleState.indeterminate)}
                          disabled={!canUpdate || saving || modulePerms.length === 0}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <ExtensionIcon fontSize="small" color="primary" />
                          <Typography variant="subtitle2" fontWeight="medium">
                            {module.name}
                          </Typography>
                          <Chip
                            label={t('roles:permissions_manager.module_permissions', { 
                              current: effectiveCount, 
                              total: modulePerms.length 
                            })}
                            size="small"
                            color={effectiveCount > 0 ? 'secondary' : 'default'}
                            variant="outlined"
                          />
                          {currentCount !== effectiveCount && (
                            <Chip
                              label={`${effectiveCount > currentCount ? '+' : ''}${effectiveCount - currentCount}`}
                              size="small"
                              color={effectiveCount > currentCount ? 'success' : 'error'}
                            />
                          )}
                        </Box>
                      }
                      sx={{ flex: 1, mr: 0 }}
                    />

                    {modulePerms.length > 0 && (
                      <Box
                        onClick={() => toggleModuleExpansion(module.module_id)}
                        sx={{
                          cursor: 'pointer',
                          p: 0.5,
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'action.selected' }
                        }}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </Box>
                    )}
                  </Box>

                  <Collapse in={isExpanded}>
                    <Box pl={6} pb={2}>
                      {Object.entries(groupedPermissions).map(([resource, permissions]) => (
                        <Box key={resource} mb={2}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontWeight: 'medium',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5
                            }}
                          >
                            {resource}
                          </Typography>
                          <Box ml={2}>
                            {permissions.map(permission => {
                              const isEffectivelyAssigned = getEffectivePermissionState(permission.permission_id);
                              const isCurrentlyAssigned = rolePermissions.some(rp => rp.permission_id === permission.permission_id);
                              const hasPendingChange =
                                pendingChanges.toAdd.includes(permission.permission_id) ||
                                pendingChanges.toRemove.includes(permission.permission_id);

                              return (
                                <FormControlLabel
                                  key={permission.permission_id}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={isEffectivelyAssigned}
                                      onChange={() => handlePermissionToggle(permission, isEffectivelyAssigned)}
                                      disabled={!canUpdate || saving}
                                      sx={{
                                        ...(hasPendingChange && {
                                          color: isEffectivelyAssigned !== isCurrentlyAssigned ?
                                            (isEffectivelyAssigned ? 'success.main' : 'error.main') : undefined
                                        })
                                      }}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2">
                                          {permission.action}
                                        </Typography>
                                        {hasPendingChange && (
                                          <Chip
                                            label={isEffectivelyAssigned !== isCurrentlyAssigned ?
                                              (isEffectivelyAssigned ? t('roles:permissions_manager.add_label') : t('roles:permissions_manager.remove_label')) : ''}
                                            size="small"
                                            color={isEffectivelyAssigned !== isCurrentlyAssigned ?
                                              (isEffectivelyAssigned ? 'success' : 'error') : 'default'}
                                            sx={{ height: 16, fontSize: '0.6rem' }}
                                          />
                                        )}
                                      </Box>
                                      {permission.description && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {permission.description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    ml: 0,
                                    mb: 0.5
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Collapse>

                  {index < myDepartmentModules.length - 1 && <Divider sx={{ my: 1 }} />}
                </Box>
              );
            })}
          </FormGroup>
        )}
      </CardContent>
    </Card>
  );
}